import "handsontable/dist/handsontable.full.min.css";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { UserHasAcl } from "../../Utils/helpers";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { assetService, cacheService } from "../../Core/Api/services";
import { UpdateMetadataFieldsRequest } from "../../Core/Api/Types/update-metadata-fields-request";
import moment from "moment";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import React from "react";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import AudioTrackMappingCard from "../Cards/AudioTrackMappingCard";
import { AudioChannel } from "../../Pages/Timetool/custom-timeline-action";
import { useTranslation } from "react-i18next";

interface AudioTracksListProps {
  asset?: DbAssetObjectInfos;
  audioChannels?: AudioChannel[];
  onChange?: (metadata: IDbBaseMetadataValueInfos) => void;
}

const AudioTracksList: React.FC<AudioTracksListProps> = (props) => {
  const { t } = useTranslation();
  const metadataFromStorage: DbMetadataFieldInfos[] = cacheService.GetMetadata();
  const languages = metadataFromStorage.find((m) => m.tagName === "languageAudio");
  const languagesValues = languages?.values?.map((lang) => ({ value: lang.value!, display: lang.value! }))!;
  const allowEdition =
    props.asset &&
    (UserHasAcl(AclTypes.aclSuperUser) ||
      (props.asset.classification === ObjectClass.Raw && UserHasAcl(AclTypes.aclMetadataAudioTracksUpdate)));
  const enqueueCustomSnackbar = useCustomSnackbar();

  // Make a copy of object to be able to modify it
  let metadata = GetMetadataByTagName(props.asset, "audioChannels");

  if (!props.audioChannels && (!metadata || !metadata.value)) return <></>;

  let audioChannels: any[] = props.audioChannels
    ? JSON.parse(JSON.stringify(props.audioChannels))
    : JSON.parse(JSON.stringify(metadata?.value));

  audioChannels.forEach((channel) => {
    if (channel.language === null)
      channel.language = {
        id: 0,
        externalId: 0,
        value: "",
        label: "",
        rfc5646: "",
      };
  });

  const handleSave = (newValues) => {
    audioChannels[newValues.id].bitDepth = newValues.bitDepth;
    audioChannels[newValues.id].isDolbyEEncoded = newValues.dolbyE;
    audioChannels[newValues.id].format = newValues.format;
    audioChannels[newValues.id].language.value = newValues.language;
    audioChannels[newValues.id].loudnessValue = newValues.loudness;
    audioChannels[newValues.id].lra = newValues.lra;
    audioChannels[newValues.id].channelPosition = newValues.position;
    audioChannels[newValues.id].isLoudnessCorrected = newValues.r128;
    audioChannels[newValues.id].sampling = newValues.sampling;
    audioChannels[newValues.id].truePeaks = newValues.truePeaks;
    audioChannels[newValues.id].version = newValues.version;

    let update: IDbBaseMetadataValueInfos = {
      $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAudioChannelsMetadataValueInfos, VDM.AirLab.Sdk.Core",
      id: metadata?.id!,
      fieldType: metadata?.fieldType!,
      creationTime: moment().toDate(),
      updateTime: null,
      fieldName: null,
      fieldLabel: null,
      targetLayer: ObjectLayer.Asset,
      valueLabel: null,
      value: audioChannels,
    };

    let request: UpdateMetadataFieldsRequest = {
      objectId: props.asset?.id || null,
      metadata: [update],
    };

    assetService.UpdateMetadataFields(request).then((response) => {
      if (response && response.success) {
        enqueueCustomSnackbar(t("alert:success.audioChannelsUpated"), "success");
        props.onChange && props.onChange(request.metadata![0]);
      }
    });
  };

  const versions = [
    { value: "OV", display: "OV" },
    { value: "IV", display: "IV" },
    { value: "MusicAndEffects", display: "MusicAndEffects" },
    { value: "Dubbed", display: "Dubbed" },
    { value: "AD", display: "AD" },
    { value: "Unused", display: "Unused" },
    { value: "Undefined", display: "Undefined" },
    { value: "Commentary", display: "Commentary" },
  ];

  const positions = [
    { value: "lt", display: "Lt" },
    { value: "Rt", display: "Rt" },
    { value: "L", display: "L" },
    { value: "R", display: "R" },
    { value: "C", display: "C" },
    { value: "LFE", display: "LFE" },
    { value: "Ls", display: "Ls" },
    { value: "Rs", display: "Rs" },
    { value: "Lss", display: "Lss" },
    { value: "Rss", display: "Rss" },
    { value: "Lrs", display: "Lrs" },
    { value: "Lt_Rt", display: "Lt_Rt" },
    { value: "Lt_Rt_C_LFE_Ls_Rs", display: "Lt_Rt_C_LFE_Ls_Rs" },
  ];

  const formats = [
    { value: "PCM", display: "PCM" },
    { value: "DolbyE", display: "DolbyE" },
    { value: "AAC", display: "AAC" },
    { value: "AC3", display: "AC3" },
  ];

  return (
    <>
      {audioChannels.map((x, i) => (
        <AudioTrackMappingCard
          id={i}
          version={x.version}
          versionOptions={versions}
          language={x.language.value}
          languageOptions={languagesValues}
          position={x.channelPosition}
          positionOptions={positions}
          sampling={x.sampling}
          bitDepth={x.bitDepth}
          format={x.format}
          formatOptions={formats}
          r128={x.isLoudnessCorrected}
          dolbyE={x.isDolbyEEncoded}
          loudness={x.loudnessValue}
          lra={x.lra}
          truePeaks={x.truePeaks}
          disableSave={!allowEdition}
          onEditing={handleSave}
        />
      ))}
    </>
  );
};

export default AudioTracksList;
